import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { CardLayout, DeleteModal, MTable } from "../v3";
import { getStageTableColumns } from "../../pages/project/constants";
import { AnalyticsService, ProjectService } from "../../services";
import { AddEditProjectStage } from ".";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { DELETE_STAGE, EDIT_STAGE } from "../../constants";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectStages = ({ reloadData }) => {
  const [projectStages, setProjectStages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [projectStageId, setProjectStageId] = useState(null);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-stages",
    defaults: {},
  });
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    projectStageId: null,
  });

  const { id } = useParams();
  const { user } = useSelector((state) => state.user);

  const hasEditStage = useHasPermission(EDIT_STAGE);
  const hasDeleteStage = useHasPermission(DELETE_STAGE);

  const fetchData = useCallback(
    async (projectStageFilters = {}) => {
      setLoading(true);
      try {
        const { data: projectStageSummary } = await AnalyticsService.getProjectStageSummary({
          ...projectStageFilters,
          projectIds: id,
        });
        setProjectStages(projectStageSummary.rows);
        setTotalCount(projectStageSummary.count);
      } catch (error) {
        toast.error("Failed to fetch project stages");
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
    });
  }, [fetchData, paginationState, reloadData]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Stages" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Stages",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleEdit = async (data) => {
    setProjectStageId(data.id);
    setOpenModal(true);
  };

  const openDeleteModal = (entity) => {
    setDeleteModal({
      isOpen: true,
      projectStageId: entity?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      projectStageId: null,
    });
  };

  const deleteProjectStage = async () => {
    try {
      await ProjectService.deleteProjectStage(deleteModal.projectStageId);
      toast.success("Project stage deleted successfully");
      closeDeleteModal();
      fetchData({
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
      });
    } catch (error) {
      console.error("🚀 ~ deleteProjectStage ~ error:", error);
      toast.error("Failed to delete project stage");
    }
  };

  const handleActions = (data, actionType) => {
    if (actionType === "edit") handleEdit(data);
    else if (actionType === "delete") openDeleteModal(data);
  };

  const columnHeaders = getStageTableColumns(handleActions, hasEditStage, hasDeleteStage);

  const rows = projectStages.map((item, index) => {
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: item.projectStageId,
      stageId: item?.projectStageName,
      estimatedAmount: item.estimatedAmount,
      expense: item.projectStageExpense,
      income: item.projectStageIncome,
      profit: item.projectStageProfit,
      balance: item.projectStageBalance,
    };
  });

  return (
    <>
      <CardLayout>
        <MTable
          // Search data
          placeholder="Search stage"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          // Export data
          showExportButton // TODO: implement permission
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
        />
      </CardLayout>

      {/* Edit Modal */}
      <AddEditProjectStage
        open={openModal}
        onClose={() => setOpenModal(false)}
        projectStageId={projectStageId}
        onSuccess={() =>
          fetchData({
            page: paginationState.page + 1,
            limit: paginationState.pageSize,
          })
        }
      />
      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={deleteProjectStage}
        message="Want to delete this stage?"
      />
    </>
  );
};
